import { useEffect, useRef, useState } from "react";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import { createLookupBatcher } from "./contactLookupBatcher";
import { listingSellerStore } from "./listingSellerStore";
import { CONTACTS_LISTING_SELLERS_MAX_IDS } from "../../@types/webotServer";
import type { ContactsListingSellersResponse } from "../../@types/webotServer";
import type { ListingSellersValue } from "./listingSellerStore";

/**
 * WHICH OF MY CONTACTS IS THIS LISTING'S SELLER / LANDLORD — by the listing's id, not its phone.
 *
 * ```tsx
 * const { sellers, failed } = useListingSellerContacts(listingId);
 * // undefined → still asking (or failed: see `failed`) · [] → nobody of mine · [c, …] → saved
 * ```
 *
 * The listings board's save-owner button reads this beside `useContactByPhone`: the phone
 * answers "is the number on the ad a contact of mine", this answers "did I already save this
 * listing's owner" — under whatever number she typed. See `listingSellerStore` for why both.
 *
 * Every listing asked about in a tick leaves as ONE request (chunked to the server's cap, three
 * at a time), so a board page of 200 rows is one `POST /contacts/listing-sellers`, and the row
 * and the grid card of one listing share one answer. A reader re-renders only when ITS listing's
 * answer changes, never on every answer in the store (the board mounts up to 200 cards).
 */

/** The latest `contactsListingSellers` — `useWebotServer()` rebuilds its functions on every render. */
let fetchSellers: ((listingIds: string[]) => Promise<ContactsListingSellersResponse>) | null = null;

const batcher = createLookupBatcher({
  chunkSize: CONTACTS_LISTING_SELLERS_MAX_IDS,
  concurrency: 3,
  send: async (chunk) => {
    // Captured when the request LEAVES: its token is the account signed in now.
    const session = listingSellerStore.session();
    if (!fetchSellers) {
      listingSellerStore.release(session, chunk);
      return;
    }
    try {
      const response = await fetchSellers(chunk);
      listingSellerStore.record(session, chunk, response.contacts ?? []);
    } catch (error) {
      // Released, never recorded as "nobody of mine": that is the answer a screen acts on.
      listingSellerStore.release(session, chunk);
      // eslint-disable-next-line no-console
      console.error("useListingSellerContacts: failed to ask for listing sellers", error);
    }
  }
});

/** Binds the store to the signed-in account; a switch also drops whatever has not left yet. */
const bindAccount = (account: string | null): void => {
  const before = listingSellerStore.session();
  listingSellerStore.bindAccount(account);
  if (listingSellerStore.session() !== before) batcher.clear();
};

export type ListingSellerContacts = {
  /** `undefined` = no answer yet (or the request failed). `[]` = answered: nobody of mine. */
  sellers: ListingSellersValue | undefined;
  /** The last request for this listing failed: no answer is held and none is on its way. */
  failed: boolean;
};

type Snapshot = { sellers: ListingSellersValue | undefined; failed: boolean };

const snapshotOf = (listingId: string | null): Snapshot =>
  listingId
    ? { sellers: listingSellerStore.read(listingId), failed: listingSellerStore.hasFailed(listingId) }
    : { sellers: undefined, failed: false };

export default function useListingSellerContacts(
  listingId: string | null | undefined,
  enabled = true
): ListingSellerContacts {
  const { contactsListingSellers } = useWebotServer();
  const { user } = useAuth();
  const account = (user?.email as string | undefined) ?? null;
  const id = listingId || null;

  // During render, and idempotent: the store must belong to THIS account before it is read.
  bindAccount(account);

  const fetcher = useRef(contactsListingSellers);
  fetcher.current = contactsListingSellers;

  // Re-render when THIS listing's answer changes — not on every answer that lands in the store.
  const [, setTick] = useState(0);
  const seen = useRef<Snapshot>(snapshotOf(id));
  seen.current = snapshotOf(id);
  useEffect(
    () =>
      listingSellerStore.subscribe(() => {
        const next = snapshotOf(id);
        if (next.sellers === seen.current.sellers && next.failed === seen.current.failed) return;
        seen.current = next;
        setTick((value) => value + 1);
      }),
    [id]
  );

  const askKey = enabled && account && id ? `${account}\n${id}` : null;
  useEffect(() => {
    if (!askKey || !id) return;
    fetchSellers = (listingIds) => fetcher.current(listingIds);
    const claimed = listingSellerStore.claim([id]);
    if (claimed.length > 0) batcher.request(claimed);
  }, [askKey, id]);

  return account ? snapshotOf(id) : { sellers: undefined, failed: false };
}
